import { APIKit } from 'config/axios/index';
import store from 'config/redux/index';
import { start, end } from 'config/redux/slice';

import { ServerConstants, ServiceConstants } from 'constant';
import { ServiceModel } from 'models';

import { handleErrorWithTokenRenewal } from './common';

const { API_V1_ABSENCE } = ServerConstants;
const {
  REQUEST_ABSENCE_LIST_ID,
  REQUEST_ABSENCE_VIEW_ID,
  REQUEST_ABSENCE_CREATE_ID,
  REQUEST_ABSENCE_UPDATE_ID,
  REQUEST_ABSENCE_DELETE_ID,
  REQUEST_ABSENCE_APPROVE_ID,
  REQUEST_ABSENCE_REJECT_ID,
  REQUEST_ABSENCE_CANCEL_ID,
  REQUEST_ABSENCE_OPTIONS_ID,
  REQUEST_ABSENCE_NUMBER_ID,
  REQUEST_ABSENCE_REPLACEMENT_AGENTS_ID,
} = ServiceConstants;

// Types
export interface AbsenceRequest {
  agentId: number;
  replacementAgentId?: number;
  dateFrom: string;
  dateTo: string;
  reason?: string;
  note?: string;
}

export interface AbsenceListParams {
  page?: number;
  pageSize?: number;
  agentId?: number;
  myAbsence?: boolean;
}

export interface AbsenceReplacementAgentsParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

// List absences
export async function absenceList(params?: AbsenceListParams) {
  const requestId = REQUEST_ABSENCE_LIST_ID;
  try {
    store.dispatch(start(requestId));
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) {
      searchParams.append('page', String(params.page));
    }
    if (params?.pageSize !== undefined) {
      searchParams.append('pageSize', String(params.pageSize));
    }
    if (params?.agentId !== undefined) {
      searchParams.append('agentId', String(params.agentId));
    }
    if (params?.myAbsence !== undefined) {
      searchParams.append('myAbsence', String(params.myAbsence));
    }
    const queryString = searchParams.toString();
    const url = queryString ? `${API_V1_ABSENCE}?${queryString}` : API_V1_ABSENCE;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceList, [params], requestId);
  }
}

// List agents available as replacements for an agent currently covering another agent
export async function absenceReplacementAgents(params: AbsenceReplacementAgentsParams) {
  const requestId = REQUEST_ABSENCE_REPLACEMENT_AGENTS_ID;
  try {
    store.dispatch(start(requestId));
    const searchParams = new URLSearchParams({
      page: String(params.page ?? 0),
      pageSize: String(params.pageSize ?? 20),
      search: params.search ?? '',
    });
    const response = await APIKit.get(`${API_V1_ABSENCE}/replacement-agents?${searchParams.toString()}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
    return response.data;
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceReplacementAgents, [params], requestId);
  }
}

// Get single absence
export async function absenceOne(id: number) {
  const requestId = REQUEST_ABSENCE_VIEW_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_ABSENCE}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceOne, [id], requestId);
  }
}

// Create absence request
export async function absenceCreate(data: AbsenceRequest) {
  const requestId = REQUEST_ABSENCE_CREATE_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(API_V1_ABSENCE, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceCreate, [data], requestId);
  }
}

// Update absence request
export async function absenceUpdate(id: number, data: AbsenceRequest) {
  const requestId = REQUEST_ABSENCE_UPDATE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.put(`${API_V1_ABSENCE}/${id}`, data);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceUpdate, [id, data], requestId);
  }
}

// Delete absence request (only for PENDING status)
export async function absenceDelete(id: number) {
  const requestId = REQUEST_ABSENCE_DELETE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.delete(`${API_V1_ABSENCE}/${id}`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceDelete, [id], requestId);
  }
}

// Approve absence request (admin only)
export async function absenceApprove(id: number) {
  const requestId = REQUEST_ABSENCE_APPROVE_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_ABSENCE}/${id}/approve`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceApprove, [id], requestId);
  }
}

// Reject absence request (admin only)
export async function absenceReject(id: number) {
  const requestId = REQUEST_ABSENCE_REJECT_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_ABSENCE}/${id}/reject`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceReject, [id], requestId);
  }
}

// Cancel absence request (for APPROVED status)
export async function absenceCancel(id: number) {
  const requestId = REQUEST_ABSENCE_CANCEL_ID + id;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.post(`${API_V1_ABSENCE}/${id}/cancel`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceCancel, [id], requestId);
  }
}

// Get absence options (status enum values)
export async function absenceOptions() {
  const requestId = REQUEST_ABSENCE_OPTIONS_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(`${API_V1_ABSENCE}/options`);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceOptions, [], requestId);
  }
}

// Get absence request count
export async function absenceNumber(params?: { agentId?: number; myAbsence?: boolean }) {
  const requestId = REQUEST_ABSENCE_NUMBER_ID;
  try {
    store.dispatch(start(requestId));
    const searchParams = new URLSearchParams();
    if (params?.agentId !== undefined) {
      searchParams.append('agentId', String(params.agentId));
    }
    if (params?.myAbsence !== undefined) {
      searchParams.append('myAbsence', String(params.myAbsence));
    }
    const queryString = searchParams.toString();
    const url = queryString ? `${API_V1_ABSENCE}/number?${queryString}` : `${API_V1_ABSENCE}/number`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, absenceNumber, [params], requestId);
  }
}
